import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { AI_ADVANCED_LABEL, AI_STANDARD_LABEL } from '../lib/aiLabels';
import { appPath } from '../utils/appRouting';
import { openCookiePreferences } from '../privacy/events';
import { 
  Check, 
  X, 
  Sparkles, 
  ChevronRight,
  Zap,
  Crown,
  Star
} from 'lucide-react';

const Pricing = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');

  const plans = [
    {
      id: 'base',
      name: 'Base',
      price: 7.99,
      period: '/mese',
      description: 'Ideale per iniziare',
      icon: Star,
      color: 'blue',
      popular: false,
      features: [
        { text: '1 alunno', included: true },
        { text: '50 crediti AI/mese', included: true },
        { text: 'PEI completo', included: true },
        { text: 'Export PDF', included: true },
        { text: 'Supporto email', included: true },
        { text: AI_STANDARD_LABEL, included: true },
        { text: AI_ADVANCED_LABEL, included: false },
        { text: 'Verbali GLO con AI', included: false },
      ]
    },
    {
      id: 'pro',
      name: 'Pro',
      price: 12.99,
      period: '/mese',
      description: 'Per chi vuole di più',
      icon: Zap,
      color: 'indigo',
      popular: true,
      features: [
        { text: 'Fino a 2 alunni', included: true },
        { text: '150 crediti AI/mese', included: true },
        { text: 'PEI completo', included: true },
        { text: 'Export PDF', included: true },
        { text: 'Supporto prioritario', included: true },
        { text: AI_STANDARD_LABEL, included: true },
        { text: AI_ADVANCED_LABEL, included: false },
        { text: 'Verbali GLO con AI', included: true },
      ]
    },
    {
      id: 'premium',
      name: 'Premium',
      price: 19.99,
      period: '/mese',
      description: 'Tutto illimitato',
      icon: Crown,
      color: 'amber',
      popular: false,
      features: [
        { text: 'Alunni illimitati', included: true },
        { text: 'Crediti AI illimitati (Fair Use anti-abuso)', included: true },
        { text: 'Tutte le funzionalità', included: true },
        { text: 'Export PDF', included: true },
        { text: 'Supporto dedicato', included: true },
        { text: AI_STANDARD_LABEL, included: true },
        { text: AI_ADVANCED_LABEL, included: true },
        { text: 'Formazione personalizzata', included: true },
      ]
    }
  ];

  const handleSelectPlan = async (planId) => {
    setSelectedPlan(planId);
    // Redirect alla pagina di registrazione con il piano selezionato
    navigate(`/register?plan=${planId}&billing=${billingCycle}`);
  };

  const getDisplayPrice = (monthlyPrice) => {
    if (billingCycle === 'annual') return monthlyPrice * 10;
    return monthlyPrice;
  };

  const getPeriodLabel = () => {
    if (billingCycle === 'annual') return '/anno';
    return '/mese';
  };

  const colorClasses = {
    blue: {
      bg: 'bg-card',
      border: 'border-border',
      icon: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
      button: 'bg-blue-600 hover:bg-blue-700',
      badge: 'bg-blue-500/10 text-blue-600 dark:text-blue-400'
    },
    indigo: {
      bg: 'bg-card',
      border: 'border-border',
      icon: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400',
      button: 'bg-indigo-600 hover:bg-indigo-700',
      badge: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400'
    },
    amber: {
      bg: 'bg-card',
      border: 'border-border',
      icon: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
      button: 'bg-amber-600 hover:bg-amber-700',
      badge: 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
    }
  };

  return (
    <div className="h-[100dvh] overflow-y-auto bg-gradient-to-b from-muted/30 to-background text-foreground scroll-smooth">
      {/* Header */}
      <header className="bg-background border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to={appPath('/')} className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-brand to-brand-2 rounded-xl flex items-center justify-center shadow-elev1">
                <span className="text-yellow-400 font-bold text-lg">S+</span>
              </div>
              <div>
                <h1 className="text-lg font-bold text-foreground">SOSTEGNO PRO+</h1>
              </div>
            </Link>
            <div className="flex items-center gap-4">
              <Link
                to={appPath('/login')}
                className="inline-flex items-center h-10 text-muted-foreground hover:text-foreground font-medium leading-none"
              >
                Accedi
              </Link>
              <Button
                onClick={() => navigate(appPath('/register'))}
                className="ds-btn-gradient"
              >
                Registrati
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Pricing Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Sparkles className="w-4 h-4" />
              Piani e Prezzi
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Scegli il piano perfetto per te
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Inizia con una prova gratuita di 7 giorni. Nessuna carta richiesta.
            </p>

            {/* Billing toggle */}
            <div className="mt-6 flex items-center justify-center">
              <div className="inline-flex items-center rounded-2xl border-2 border-border bg-card/90 p-1.5 shadow-sm">
                <Button
                  type="button"
                  size="lg"
                  variant="ghost"
                  onClick={() => setBillingCycle('monthly')}
                  className={`h-12 rounded-xl px-8 text-base font-semibold transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background ${billingCycle === 'monthly' ? 'bg-brand text-white hover:brightness-110 shadow-sm shadow-brand/20' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  Mensile
                </Button>
                <Button
                  type="button"
                  size="lg"
                  variant="ghost"
                  onClick={() => setBillingCycle('annual')}
                  className={`h-12 rounded-xl px-8 text-base font-semibold transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background ${billingCycle === 'annual' ? 'bg-brand text-white hover:brightness-110 shadow-sm shadow-brand/20' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  Annuale
                </Button>
              </div>
            </div>

            {billingCycle === 'annual' && (
              <p className="mt-3 text-sm text-muted-foreground">
                Annuale: paghi <strong>10 mesi</strong>, <strong>2 mesi</strong> sono in omaggio.
              </p>
            )}
          </div>

          {/* Plans Grid */}
          <div className="mt-8 md:mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 max-w-5xl mx-auto items-stretch">
            {plans.map((plan) => {
              const Icon = plan.icon;
              const colors = colorClasses[plan.color];
              
              return (
                <div 
                  key={plan.id}
                  className={`relative h-full flex flex-col rounded-2xl border-2 ${plan.popular ? colors.border : 'border-border'} bg-card p-6 md:p-8 shadow-lg hover:shadow-xl transition-all duration-300 ${plan.popular ? 'md:scale-105 md:-mt-4 md:mb-4 mt-8 order-first md:order-none' : ''}`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap z-10">
                      <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-xs sm:text-sm font-semibold shadow-sm">
                        Più popolare
                      </span>
                    </div>
                  )}

                  {/* Plan Header */}
                  <div className={`text-center ${plan.popular ? 'mt-2' : ''} mb-6`}>
                    <div className={`w-12 h-12 md:w-14 md:h-14 ${colors.icon} rounded-xl flex items-center justify-center mx-auto mb-4`}>
                      <Icon className="w-6 h-6 md:w-7 md:h-7" />
                    </div>
                    <h3 className="text-lg md:text-xl font-bold text-foreground mb-1">{plan.name}</h3>
                    <p className="text-muted-foreground text-sm">{plan.description}</p>
                  </div>

                  {/* Price */}
                  <div className="text-center mb-6">
                    <div className="flex items-baseline justify-center">
                      <span className="text-3xl md:text-4xl font-bold text-foreground">€{getDisplayPrice(plan.price).toFixed(2)}</span>
                      <span className="text-muted-foreground ml-1">{getPeriodLabel()}</span>
                    </div>

                    {billingCycle === 'annual' && (
                      <div className="mt-2 text-xs text-muted-foreground">
                        Equivalente a €{plan.price.toFixed(2)}/mese • 2 mesi omaggio
                      </div>
                    )}
                  </div>

                  {/* Features */}
                  <ul className="flex-1 space-y-2 md:space-y-3 mb-6 md:mb-8">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 md:gap-3">
                        {feature.included ? (
                          <Check className="w-4 h-4 md:w-5 md:h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        ) : (
                          <X className="w-4 h-4 md:w-5 md:h-5 text-muted-foreground/40 flex-shrink-0 mt-0.5" />
                        )}
                        <span className={`text-sm md:text-base ${feature.included ? 'text-foreground' : 'text-muted-foreground opacity-80'}`}>
                          {feature.text}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <Button
                    onClick={() => handleSelectPlan(plan.id)}
                    className={`w-full ${colors.button} text-white text-sm md:text-base`}
                    disabled={loading}
                  >
                    Inizia con {plan.name}
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              );
            })}
          </div>

          {/* FAQ / Additional Info */}
          <div className="mt-12 md:mt-16 text-center">
            <p className="text-muted-foreground mb-4">
              Hai domande?{' '}
              <Link to={appPath('/login')} className="text-blue-600 hover:underline font-medium">Contattaci</Link>
            </p>
            <div className="flex flex-wrap justify-center gap-4 md:gap-6 text-xs md:text-sm text-muted-foreground">
              <span className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                Cancella quando vuoi
              </span>
              <span className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                Nessun costo nascosto
              </span>
              <span className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                Supporto in italiano
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* AI Credits Info */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-card rounded-2xl border border-border p-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-foreground mb-2">Come funzionano i Crediti AI?</h3>
                <p className="text-muted-foreground mb-4">
                  I crediti AI ti permettono di usare le funzionalità di intelligenza artificiale per generare contenuti personalizzati.
                </p>
                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                  <div className="bg-muted/40 rounded-lg p-4">
                    <p className="font-medium text-foreground mb-2">Esempi di utilizzo:</p>
                    <ul className="space-y-1 text-muted-foreground">
                      <li>• Suggerimenti PEI: 5 crediti</li>
                      <li>• Materiale didattico: 3 crediti</li>
                      <li>• Mappe concettuali: 4 crediti</li>
                      <li>• Verbale GLO: 8 crediti</li>
                    </ul>
                  </div>
                  <div className="bg-muted/40 rounded-lg p-4">
                    <p className="font-medium text-foreground mb-2">Buono a sapersi:</p>
                    <ul className="space-y-1 text-muted-foreground">
                      <li>• I crediti si rinnovano ogni mese</li>
                      <li>• Piano Premium = crediti illimitati (Fair Use)</li>
                      <li>• Puoi fare upgrade in qualsiasi momento</li>
                    </ul>
                    <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
                      I piani con crediti AI illimitati sono soggetti a una Fair Use Policy per prevenire abusi (es. rallentamenti
                      o limiti temporanei in caso di uso anomalo/automatizzato). Dettagli nei{' '}
                      <Link to={appPath('/terms-and-conditions')} className="text-blue-600 hover:underline font-medium">
                        Termini e Condizioni
                      </Link>
                      .
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg flex items-center justify-center">
                <span className="text-yellow-400 font-bold text-sm">S+</span>
              </div>
              <span className="text-muted-foreground">© 2025 SOSTEGNO PRO+</span>
            </div>
            <div className="flex gap-6 text-sm text-muted-foreground">
              <Link to={appPath('/privacy-policy')} className="hover:text-foreground">Privacy</Link>
              <Link to={appPath('/terms-and-conditions')} className="hover:text-foreground">Termini</Link>
              <Link to={appPath('/note-legali-ai')} className="hover:text-foreground">Note legali AI</Link>
              <Link to={appPath('/cookie-policy')} className="hover:text-foreground">Cookie</Link>
              <button type="button" onClick={openCookiePreferences} className="hover:text-foreground">
                Preferenze cookie
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Pricing;
